import { css } from "emotion";
import produce from "immer";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Icon,
  IconButton,
  IStackProps,
  ITag,
  List,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  ScrollablePane,
  Separator,
  Stack,
  TagPicker
} from "office-ui-fabric-react";
import React from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from "react-beautiful-dnd";
import {
  Field,
  InjectedFormProps,
  reduxForm,
  SubmissionError
} from "redux-form";
import { AssignFilterItem } from "../../../pages/assign/assign-page.store";
import { getOptions } from "../../../pages/assign/components/grouping-select-dropdown/GroupingSelectDropdown";
import { DialogProps } from "../../../types/types";
import { isEmptyList } from "../../../util/util";
import { renderDrowdown, renderTextField } from "../../FormFields";

export const customFilterManagementFormId = "CustomFilterRegistrationForm";

export interface CustomFilterManagementDialogProps {
  filters: AssignFilterItem[];
  onReorderFilters: (filters: AssignFilterItem[]) => void;
  onSaveButtonClick: (filters: AssignFilterItem[]) => void;
  onDeleteButtonClick: (filterId: string) => void;
  dialogProps: DialogProps;
  submit: (values: any) => void;
  close: () => void;
}

/**
 * カスタムフィルター登録ダイアログ
 * @param props
 * @constructor
 */
export const CustomFilterManagementDialog = (
  props: CustomFilterManagementDialogProps
) => {
  const {
    filters,
    onReorderFilters,
    onDeleteButtonClick,
    dialogProps,
    submit,
    close
  } = props;

  const Form = (
    formProps: InjectedFormProps<{ filters: AssignFilterItem[] }>
  ) => {
    const { handleSubmit, submitting } = formProps;

    const columnProps: Partial<IStackProps> = {
      tokens: { childrenGap: 10 },
      styles: { root: { width: "100%" } }
    };

    const onRenderCell = (filter?: AssignFilterItem, index?: number) => {
      if (!filter || index === undefined) {
        console.log("RETURN", { filter, index });
        return;
      }

      const defaultItems: ITag[] = filter.customFilterCondition
        ? filter.customFilterCondition.map(v => ({
            key: v.key,
            name: v.value
          }))
        : [];

      const onResolveSuggestionsDummy: any = {};

      const onDelete = (i: number) => () => {
        onDeleteButtonClick(filters[i].id);
      };

      return (
        <Draggable key={filter.id} draggableId={filter.id} index={index}>
          {provided => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
            >
              <Stack
                horizontal={true}
                verticalAlign={"center"}
                tokens={{ childrenGap: 10 }}
              >
                {/*Sort DragObject*/}
                <Icon iconName="Sort" />
                <IconButton
                  iconProps={{ iconName: "Delete" }}
                  onClick={onDelete(index)}
                />
                <Field
                  name={`filters[${index}].label`}
                  type="text"
                  component={renderTextField}
                  className={css`
                    width: 300px;
                  `}
                />
                <Field
                  name={`filters[${index}].defaultGroupingKey`}
                  component={renderDrowdown({ options: getOptions() })}
                  className={css`
                    width: 120px;
                  `}
                />
                <TagPicker
                  disabled={true}
                  onResolveSuggestions={onResolveSuggestionsDummy}
                  defaultSelectedItems={defaultItems}
                  styles={{ root: { width: "100%" } }}
                />
              </Stack>
              <Separator />
            </div>
          )}
        </Draggable>
      );
    };

    const onDragEnd = (result: DropResult) => {
      const ordered = produce(filters, draft => {
        if (result.destination) {
          const from = result.source.index;
          const to = result.destination.index;
          const [removed] = draft.splice(from, 1);
          draft.splice(to, 0, removed);
        }
      });
      onReorderFilters(ordered);
    };

    const filterNotFound = isEmptyList(filters);

    return (
      <Dialog
        hidden={!dialogProps.isOpen}
        onDismiss={close}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: dialogProps.title
        }}
        modalProps={{
          isBlocking: dialogProps.isModal,
          // 元々Dialogコンポーネントに設定されているmax-widthも拡張しないとダメ
          containerClassName: css`
            height: 80vh;
            width: 75vw;
            max-width: 75vw;
          `
        }}
      >
        <form
          id="customFilterManagementForm"
          // TODO: 共通化
          // https://github.com/erikras/redux-form/issues/3949
          onSubmit={handleSubmit(values => {
            return new Promise((resolve, reject) => {
              const errors = validate(values);
              if (errors) {
                console.log({ errors });
                reject(new SubmissionError(errors));
              } else {
                console.log({ values });
                submit(values);
                resolve();
              }
            });
          })}
          noValidate={true}
        >
          <div
            className={css`
              height: 60vh;
            `}
          >
            <ScrollablePane>
              <Stack horizontal={true} tokens={{ childrenGap: 50 }}>
                <Stack {...columnProps}>
                  {(() => {
                    if (filterNotFound) {
                      return (
                        <MessageBar
                          messageBarType={MessageBarType.severeWarning}
                        >
                          カスタムフィルターが登録されていません
                        </MessageBar>
                      );
                    } else {
                      return (
                        <>
                          <MessageBar>
                            フィルターの表示順はドラッグアンドドロップで変更できます（即時反映）
                          </MessageBar>
                          <MessageBar
                            messageBarType={MessageBarType.severeWarning}
                          >
                            フィルター条件変更機能は未実装です。フィルター名とグルーピング条件しか変更できません。
                          </MessageBar>
                        </>
                      );
                    }
                  })()}
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable">
                      {provided => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          <List items={filters} onRenderCell={onRenderCell} />
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Stack>
              </Stack>
            </ScrollablePane>
          </div>
        </form>
        <DialogFooter>
          <PrimaryButton
            text="保存"
            type="submit"
            form="customFilterManagementForm"
            disabled={submitting || filterNotFound}
          />
          <DefaultButton onClick={close} text="閉じる" disabled={submitting} />
        </DialogFooter>
      </Dialog>
    );
  };

  const ConnectedForm = reduxForm({
    form: customFilterManagementFormId,
    initialValues: { filters },
    destroyOnUnmount: false,
    enableReinitialize: true,
    touchOnBlur: false
  })(Form);
  return <ConnectedForm onSubmit={submit} />;
};

const validate = (values: any) => {
  const filters = values.filters as AssignFilterItem[];
  const errors: any = {};
  errors.filters = [];

  filters.forEach((filter, i) => {
    if (!filter.label) {
      errors.filters[i] = { label: "フィルター名は必須です" };
    }
  });

  return isEmptyList(errors.filters) ? undefined : errors;
};
